import * as THREE from 'three';
import { PointerLockControls } from 'three/addons/controls/PointerLockControls.js';
import { World } from './world';
import { blocks } from './blocks';
import { settingsStore } from './settingsStore';
import { InventorySingleton } from './inventory';
import { TouchControls } from './TouchControls';
import { isMobileDevice } from './device';

import { AudioManagerInstance } from './audioManager';


const CENTER_SCREEN = new THREE.Vector2();

export class Player {
  height = 1.75;
  radius = 0.5;
  maxSpeed = 5;

  jumpSpeed = 10;
  sprinting = false;
  onGround = false;
  isPaused = true;
  isMobile = false;
  controlsEnabled = true;

  scene = null;
  userId = null
  input = new THREE.Vector3();
  velocity = new THREE.Vector3();
  #worldVelocity = new THREE.Vector3();

  camera = new THREE.PerspectiveCamera(70, window.innerWidth / window.innerHeight, 0.1, 100);
  cameraHelper = new THREE.CameraHelper(this.camera);
  controls = new PointerLockControls(this.camera, document.body);
  debugCamera = false;

  raycaster = new THREE.Raycaster(new THREE.Vector3(), new THREE.Vector3(), 0, 3);
  selectedCoords = null;
  activeBlockId = blocks.empty.id;
  lastActionTime = 0;

  tool = {
    // Group that will contain the tool mesh
    container: new THREE.Group(),
    // Whether or not the tool is currently animating
    animate: false,
    // The time the animation was started
    animationStart: 0,
    // The rotation speed of the tool
    animationSpeed: 0.025,
    // Reference to the current animation
    animation: null
  }

  constructor(scene, world, userId) {
    this.userId = userId

    this.world = world;
    this.position.set(32, 20, 32);
    this.cameraHelper.visible = false;
    scene.add(this.camera);
    scene.add(this.cameraHelper);

    // Hide/show instructions based on pointer controls locking/unlocking
    this.controls.addEventListener('lock', this.onCameraLock.bind(this));
    this.controls.addEventListener('unlock', this.onCameraUnlock.bind(this));

    // The tool is parented to the camera
    this.camera.add(this.tool.container);

    // Set raycaster to use layer 0 so it doesn't interact with water mesh on layer 1
    this.raycaster.layers.set(0);
    this.camera.layers.enable(1);

    // Wireframe mesh visualizing the player's bounding cylinder
    this.boundsHelper = new THREE.Mesh(
      new THREE.CylinderGeometry(this.radius, this.radius, this.height, 16),
      new THREE.MeshBasicMaterial({ wireframe: true })
    );
    this.boundsHelper.visible = false;
    scene.add(this.boundsHelper);

    // Helper used to highlight the currently active block
    const selectionMaterial = new THREE.MeshBasicMaterial({
      transparent: true,
      opacity: 0.3,
      color: 0xffffaa
    });
    const selectionGeometry = new THREE.BoxGeometry(1.01, 1.01, 1.01);
    this.selectionHelper = new THREE.Mesh(selectionGeometry, selectionMaterial);
    scene.add(this.selectionHelper);
    this.scene = scene;

    // Add event listeners for keyboard/mouse events
    document.addEventListener('keyup', this.onKeyUp.bind(this));
    document.addEventListener('keydown', this.onKeyDown.bind(this));
    document.addEventListener('mousedown', this.onMouseDown.bind(this));

    settingsStore.load(this.userId).then(() => {
      settingsStore.onChange(this.applySettingsToControls.bind(this));
      this.applySettingsToControls(settingsStore.values);
    });

    if (isMobileDevice()) {
      this.isMobile = true;
      this.touchControls = new TouchControls();
      console.log('TouchControls enabled.');
      // On mobile, we don't necessarily need pointer lock for looking
      this.isPaused = false;
    }
  }

  applySettingsToControls(settings) {
    this.controls.lookSpeed = settings.sensitivity;
    this.controls.invertY = settings.invertMouse;
  }

  canUsePointerLock() {
    return Boolean(
      this.controls?.domElement &&
      typeof this.controls.domElement.requestPointerLock === 'function'
    );
  }



  onCameraLock() {
    const overlay = document.getElementById('overlay');
    if (overlay) overlay.style.visibility = 'hidden';

    const toolbar = document.getElementById('toolbar-container');
    if (toolbar) toolbar.style.visibility = 'visible';

    this.isPaused = false;
  }

  onCameraUnlock() {
    if (!this.debugCamera) {
      const overlay = document.getElementById('overlay');
      if (overlay) overlay.style.visibility = 'visible';

      const toolbar = document.getElementById('toolbar-container');
      if (toolbar) toolbar.style.visibility = 'hidden';

      this.isPaused = true;
    }
  }

  /**
   * Updates the state of the player
   * @param {World} world 
   */
  update(world) {
    this.updateBoundsHelper();
    this.updateRaycaster(world);

    if (this.tool.animate) {
      this.updateToolAnimation();
    }

    if (this.isMobile && this.touchControls) {
      this.updateMobileLook();
    }
  }

  /**
   * Updates camera rotation based on touch look deltas
   */
  updateMobileLook() {
    const lookDelta = this.touchControls.look;
    if (lookDelta.x !== 0 || lookDelta.y !== 0) {
      const euler = new THREE.Euler(0, 0, 0, 'YXZ');
      euler.setFromQuaternion(this.camera.quaternion);

      euler.y -= lookDelta.x * 2.0; // Sensitivity
      euler.x -= lookDelta.y * 2.0;

      euler.x = Math.max(-Math.PI / 2, Math.min(Math.PI / 2, euler.x));

      this.camera.quaternion.setFromEuler(euler);

      // Reset deltas
      lookDelta.x = 0;
      lookDelta.y = 0;
    }
  }

  /**
   * Updates the raycaster used for block selection
   * @param {World} world 
   */
  updateRaycaster(world) {
    this.raycaster.setFromCamera(CENTER_SCREEN, this.camera);
    const intersections = this.raycaster.intersectObject(world, true);

    if (intersections.length > 0) {
      const intersection = intersections[0];

      // Get the chunk associated with the selected block
      const chunk = intersection.object.parent;

      // Get the transformation matrix for the selected block
      const blockMatrix = new THREE.Matrix4();
      intersection.object.getMatrixAt(intersection.instanceId, blockMatrix);

      // Set the selected coordinates to the origin of the chunk,
      // then apply the transformation matrix of the block to get
      // the block coordinates
      this.selectedCoords = chunk.position.clone();
      this.selectedCoords.applyMatrix4(blockMatrix);
      // console.log("SelectedCoords: ", this.selectedCoords)

      if (this.activeBlockId !== blocks.empty.id) {
        // If we are adding a block, move it 1 block over in the direction
        // of where the ray intersected the cube
        this.selectedCoords.add(intersection.normal);
      }

      this.selectionHelper.position.copy(this.selectedCoords);
      this.selectionHelper.visible = true;
    } else {
      this.selectedCoords = null;
      this.selectionHelper.visible = false;
    }
  }

  /**
   * Updates the state of the player based on the current user inputs
   * @param {Number} dt 
   */
  applyInputs(dt) {
    if (this.controls.isLocked === true || this.isMobile) {
      if (this.isMobile && this.touchControls) {
        this.input.x = this.touchControls.movement.right * this.maxSpeed;
        this.input.z = this.touchControls.movement.forward * this.maxSpeed;
        this.sprinting = this.touchControls.sprint;
        if (this.touchControls.jump && this.onGround) {
          this.velocity.y += this.jumpSpeed;
          this.touchControls.jump = false; // Reset jump trigger
        }
      }

      this.velocity.x = this.input.x * (this.sprinting ? 1.5 : 1);
      this.velocity.z = this.input.z * (this.sprinting ? 1.5 : 1);


      if (this.isMobile) {
        // Unified movement logic for both Mobile and Desktop to ensure XZ-plane movement
        // (Standard PointerLockControls.moveForward couples movement to pitch, which drives player into ground)
        const moveVector = new THREE.Vector3(this.velocity.x, 0, -this.velocity.z);
        const yaw = new THREE.Euler().setFromQuaternion(this.camera.quaternion, 'YXZ').y;
        moveVector.applyEuler(new THREE.Euler(0, yaw, 0));
        this.position.add(moveVector.multiplyScalar(dt));
      } else {
        this.controls.moveRight(this.velocity.x * dt);
        this.controls.moveForward(this.velocity.z * dt);
      }

      /*
      if (this.isMobile) {
        // ...
      } else {
        // Removed pitch-dependent movement
      }
      */

      this.position.y += this.velocity.y * dt;

      if (this.position.y < 0) {
        this.position.y = 0;
        this.velocity.y = 0;
      }
    }

    const infoPlayerPos = document.getElementById('info-player-position');
    if (infoPlayerPos) infoPlayerPos.innerHTML = this.toString();
  }

  /**
   * Updates the position of the player's bounding cylinder helper
   */
  updateBoundsHelper() {
    this.boundsHelper.position.copy(this.camera.position);
    this.boundsHelper.position.y -= this.height / 2;
  }

  /**
   * Set the tool object the player is holding
   * @param {THREE.Mesh} tool 
   */
  setTool(tool) {
    this.tool.container.clear();
    this.tool.container.add(tool);
    this.tool.container.receiveShadow = true;
    this.tool.container.castShadow = true;

    this.tool.container.position.set(0.6, -0.3, -0.5);
    this.tool.container.scale.set(0.5, 0.5, 0.5);
    this.tool.container.rotation.z = Math.PI / 2;
    this.tool.container.rotation.y = Math.PI + 0.2;
  }

  /**
   * Animates the tool rotation
   */
  updateToolAnimation() {
    if (this.tool.container.children.length > 0) {
      const t = this.tool.animationSpeed * (performance.now() - this.tool.animationStart);
      this.tool.container.children[0].rotation.y = 0.5 * Math.sin(t);
    }
  }

  /**
   * Returns the current world position of the player
   * @returns {THREE.Vector3}
   */
  get position() {
    return this.camera.position;
  }

  /**
   * Returns the velocity of the player in world coordinates
   * @returns {THREE.Vector3}
   */
  get worldVelocity() {
    this.#worldVelocity.copy(this.velocity);
    this.#worldVelocity.applyEuler(new THREE.Euler(0, this.camera.rotation.y, 0));
    return this.#worldVelocity;
  }

  /**
   * Applies a change in velocity 'dv' that is specified in the world frame
   * @param {THREE.Vector3} dv 
   */
  applyWorldDeltaVelocity(dv) {
    dv.applyEuler(new THREE.Euler(0, -this.camera.rotation.y, 0));
    this.velocity.add(dv);
  }

  /**
   * Event handler for 'keyup' event
   * @param {KeyboardEvent} event 
   */
  onKeyDown(event) {
    if (this.isPaused && event.code !== 'KeyX') {   // /^Digit[0-8]$/.test(event.code))
      return;
    }
    if (!this.controls.isLocked && this.canUsePointerLock()) {
      this.debugCamera = false;
      this.controls.lock();
    }

    const keyMap = {
      Digit1: 1,
      Digit2: 2,
      Digit3: 3,
      Digit4: 4,
      Digit5: 5,
      Digit6: 6,
      Digit7: 7,
      Digit8: 8,
      Digit9: 9,
      KeyF: 10,
      KeyG: 11,
      KeyH: 12,
      KeyJ: 13,
      KeyK: 14,
      KeyL: 15,
      KeyM: 16,
      Digit0: 0
    };

    if (keyMap[event.code] !== undefined) {
      const key = keyMap[event.code];
      document.getElementById(`toolbar-${this.activeBlockId}`)?.classList.remove('selected');
      if (key === 0 || InventorySingleton.getVoxelCountById(key, "available") > 0) {
        this.activeBlockId = key;
      } else {
        this.world._notify("Not in inventory! 📂");
        return;
      }
      document.getElementById(`toolbar-${key}`)?.classList.add('selected');
      this.tool.container.visible = (this.activeBlockId === 0);
      return;
    }

    switch (event.code) {
      case 'KeyW': this.input.z = this.maxSpeed; break;
      case 'KeyA': this.input.x = -this.maxSpeed; break;
      case 'KeyS': this.input.z = -this.maxSpeed; break;
      case 'KeyD': this.input.x = this.maxSpeed; break;
      case 'KeyR':
        if (this.repeat) break;
        this.position.y = 32;
        this.velocity.set(0, 0, 0);
        break;
      case 'ShiftLeft':
      case 'ShiftRight':
        this.sprinting = true;
        break;
      case 'Space':
        if (this.onGround) this.velocity.y += this.jumpSpeed;
        break;
      case 'F10':
        this.debugCamera = true;
        this.controls.unlock();
        break;
    }
  }

  /**
   * Event handler for 'keyup' event
   * @param {KeyboardEvent} event 
   */
  onKeyUp(event) {
    switch (event.code) {
      case 'KeyW':
        this.input.z = 0;
        break;
      case 'KeyA':
        this.input.x = 0;
        break;
      case 'KeyS':
        this.input.z = 0;
        break;
      case 'KeyD':
        this.input.x = 0;
        break;
      case 'ShiftLeft':
      case 'ShiftRight':
        this.sprinting = false;
        break;
    }
  }

  /**
   * Event handler for 'mousedown'' event
   * @param {MouseEvent} event 
   */
  onMouseDown(event) {
    if (this.isMobile) return; // Prevent screen taps from triggering actions on mobile (use UI buttons instead)

    // Prevent locking if controls are disabled or if clicking on UI elements (inputs, buttons)
    if (!this.controlsEnabled || (event.target && ['INPUT', 'BUTTON', 'TEXTAREA'].includes(event.target.tagName))) {
      return;
    }
    // Also check if the click is on an overlay (by checking class name or hierarchy if needed, 
    // but tagName check handles the most common "input focus" issues that cause errors)
    // A better approach is often to only listen to mousedown on the canvas explicitly, 
    // but the controls are attached to document.body by default.

    if (this.controls.isLocked) {
      this.performAction();
    } else if (this.canUsePointerLock()) {
      this.controls.lock();
    }
  }



  /**
   * Performs the primary action (break or place)
   */
  performAction() {
    const now = performance.now();
    if (now - this.lastActionTime < 300) return;
    this.lastActionTime = now;

    if (this.selectedCoords) {
      // (same content as before)
      // If active block is an empty block, then we are in delete mode
      if (this.activeBlockId === blocks.empty.id) {
        const soundObject = AudioManagerInstance.playPositionalSound('block_break', this.selectedCoords, 1.0, false, 1, 1, this.scene);
        this.world.removeBlock(
          this.selectedCoords.x,
          this.selectedCoords.y,
          this.selectedCoords.z,
          this.userId
        );
      } else {
        const soundPosition = this.selectedCoords.clone().add(new THREE.Vector3(0, 0.5, 0));
        const soundObject = AudioManagerInstance.playPositionalSound('block_place', soundPosition, 1.0, false, 1, 1, this.scene);
        this.world.addBlock(
          this.selectedCoords.x,
          this.selectedCoords.y,
          this.selectedCoords.z,
          this.activeBlockId,
          this.userId
        );
      }

      // If the tool isn't currently animating, trigger the animation
      if (!this.tool.animate) {
        this.tool.animate = true;
        this.tool.animationStart = performance.now();

        // Clear the existing timeout so it doesn't cancel our new animation
        clearTimeout(this.tool.animation);

        // Stop the animation after 1.5 cycles
        this.tool.animation = setTimeout(() => {
          this.tool.animate = false;
        }, 3 * Math.PI / this.tool.animationSpeed);
      }
    }
  }

  /**
   * Returns player position in a readable string form
   * @returns {string}
   */
  toString() {
    let str = '';
    str += `X: ${this.position.x.toFixed(3)} `;
    str += `Y: ${this.position.y.toFixed(3)} `;
    str += `Z: ${this.position.z.toFixed(3)}`;
    return str;
  }

  teleportToIsland(coords) {
    this.position.set(coords.x, coords.y + 2, coords.z);
    this.velocity.set(0, 0, 0);
    console.log(`Teleported to island at (${coords.x}, ${coords.y}, ${coords.z})`);
  }

  setControlsEnabled(enabled) {
    this.controlsEnabled = enabled;
    if (this.touchControls) {
      this.touchControls.setEnabled(enabled);
    }
    if (enabled && this.isMobile) {
      this.isPaused = false;
    }
    if (!enabled && this.controls.isLocked) {
      this.controls.unlock();
    }
  }


}
